import React from "react";
import { Q, Single, Multi, Counter, Text } from "./AudioFields";
import * as D from "../../data/audioQuote";

export const ScopeStep = ({ a, set }) => (
  <Q title="What kind of audio project is this?" testid="aq-scope">
    <Single options={D.SCOPES} value={a.scope} onChange={(v) => set({ scope: v, outdoor: v === "Outdoor area only" ? "Yes" : a.scope === "Outdoor area only" ? "" : a.outdoor })} testid="aq-scope-opt" />
  </Q>
);

export const QuickStep = ({ a, set }) => {
  const jobs = a.quick || {};
  const toggle = (j) => {
    const next = { ...jobs };
    if (j in next) delete next[j]; else next[j] = "";
    set({ quick: next });
  };
  return (
    <Q title="Which quick audio jobs do you need?" hint="Pick one or more, then choose who supplies the equipment for each." testid="aq-quick">
      <div className="space-y-3">
        {D.QUICK_JOBS.map((j, i) => (
          <div key={j} className={`rounded-xl border-2 p-4 ${j in jobs ? "border-[#3b9dff] bg-[#eef4ff]" : "border-slate-200"}`}>
            <label className="flex items-center gap-3 cursor-pointer font-semibold text-[#0a1f44] text-sm">
              <input type="checkbox" checked={j in jobs} onChange={() => toggle(j)} data-testid={`aq-quick-${i}`} className="w-4 h-4 accent-[#3b9dff]" />
              {j}
            </label>
            {j in jobs && (
              <div className="mt-3 grid sm:grid-cols-2 gap-2">
                {[D.OWN, D.SUPPLY].map((o, k) => (
                  <button key={o} type="button" onClick={() => set({ quick: { ...jobs, [j]: o } })} data-testid={`aq-quick-${i}-${k ? "supply" : "own"}`}
                    className={`rounded-lg border-2 px-3 py-2 text-xs font-semibold ${jobs[j] === o ? "border-[#3b9dff] bg-[#3b9dff] text-white" : "border-slate-200 bg-white text-slate-600 hover:border-[#3b9dff]/50"}`}>{o}</button>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </Q>
  );
};

export const LayoutStep = ({ a, set }) => (
  <>
    {a.scope !== "Outdoor area only" && (
      <Q title="Surround layout for the main media room" testid="aq-layout">
        <Single options={D.LAYOUTS} value={a.layout} onChange={(v) => set({ layout: v })} testid="aq-layout-opt" cols="sm:grid-cols-3" />
      </Q>
    )}
    <Q title="How many total zones or rooms need audio?" testid="aq-zones">
      <Counter value={a.zones} onChange={(v) => set({ zones: v })} min={1} testid="aq-zones-counter" />
    </Q>
    <Q title="Do you prefer or currently use a specific streaming ecosystem?" testid="aq-eco">
      <Single options={D.ECOSYSTEMS} value={a.eco} onChange={(v) => set({ eco: v })} testid="aq-eco-opt" />
    </Q>
  </>
);

export const SpeakersStep = ({ a, set }) => (
  <>
    <Q title="Speaker types" hint={a.zones > 1 ? `Pick every type you want across your ${a.zones} zones. Add zone details in the notes at the end.` : "Pick every type you want."} testid="aq-speakers">
      <Multi options={D.SPEAKER_TYPES} value={a.speakers} onChange={(v) => set({ speakers: v })} testid="aq-speaker" cols="sm:grid-cols-3" />
    </Q>
    <Q title="How many subwoofers need to be installed or integrated?" testid="aq-subs">
      <Counter value={a.subs} onChange={(v) => set({ subs: v })} testid="aq-subs-counter" />
    </Q>
    <Q title="What type of ceiling is in the installation space?" testid="aq-ceiling">
      <Single options={D.CEILINGS} value={a.ceiling} onChange={(v) => set({ ceiling: v })} testid="aq-ceiling-opt" />
    </Q>
  </>
);

export const SourcingStep = ({ a, set }) => (
  <>
    <Q title="Equipment sourcing" hint={D.SUPPLY_LINE} testid="aq-sourcing">
      <Single options={[D.OWN, D.SUPPLY]} value={a.sourcing} onChange={(v) => set({ sourcing: v })} testid="aq-sourcing-opt" />
    </Q>
    <div className={`grid sm:grid-cols-2 gap-3 overflow-hidden transition-all duration-300 ${a.sourcing === D.OWN ? "max-h-60 opacity-100 mt-4" : "max-h-0 opacity-0"}`}>
      <Text placeholder="What receiver or amplifier do you currently have?" value={a.ownAmp} onChange={(e) => set({ ownAmp: e.target.value })} data-testid="aq-own-amp" />
      <Text placeholder="What speaker models do you currently have?" value={a.ownSpeakers} onChange={(e) => set({ ownSpeakers: e.target.value })} data-testid="aq-own-speakers" />
    </div>
    <Q title="How would you like to control your sound system?" testid="aq-control">
      <Single options={D.CONTROLS} value={a.control} onChange={(v) => set({ control: v })} testid="aq-control-opt" />
    </Q>
  </>
);

export const SurveyStep = ({ a, set }) => (
  <>
    <Q title="Target room(s)" testid="aq-rooms">
      <Multi options={D.ROOMS} value={a.rooms} onChange={(v) => set({ rooms: v })} testid="aq-room" cols="grid-cols-2 sm:grid-cols-3" />
      {a.rooms.includes("Other") && <div className="mt-2"><Text placeholder="Other room(s)" value={a.roomsOther} onChange={(e) => set({ roomsOther: e.target.value })} data-testid="aq-rooms-other" /></div>}
    </Q>
    <Q title="Approximate room size (optional)" testid="aq-size">
      <Single options={D.ROOM_SIZES} value={a.roomSize} onChange={(v) => set({ roomSize: v === a.roomSize ? "" : v })} testid="aq-size-opt" />
    </Q>
    <Q title="Wall type" testid="aq-wall"><Single options={D.WALL_TYPES} value={a.wall} onChange={(v) => set({ wall: v })} testid="aq-wall-opt" cols="grid-cols-2 sm:grid-cols-5" /></Q>
    <Q title="TV mounting and display status" testid="aq-display"><Single options={D.DISPLAY} value={a.display} onChange={(v) => set({ display: v })} testid="aq-display-opt" cols="sm:grid-cols-3" /></Q>
    <Q title="Wire access and construction" testid="aq-access"><Multi options={D.WIRE_ACCESS} value={a.access} onChange={(v) => set({ access: v })} testid="aq-access-opt" /></Q>
    <Q title="Are there pre-existing speaker wires?" testid="aq-wires">
      <Single options={["Yes", "No"]} value={a.wires} onChange={(v) => set({ wires: v })} testid="aq-wires-opt" cols="grid-cols-2 max-w-xs" />
      <div className={`grid sm:grid-cols-2 gap-3 overflow-hidden transition-all duration-300 ${a.wires === "Yes" ? "max-h-60 opacity-100 mt-3" : "max-h-0 opacity-0"}`}>
        <Text placeholder="What wire type or gauge is run?" value={a.wireGauge} onChange={(e) => set({ wireGauge: e.target.value })} data-testid="aq-wire-gauge" />
        <Text placeholder="Where do the wires currently end?" value={a.wireEnd} onChange={(e) => set({ wireEnd: e.target.value })} data-testid="aq-wire-end" />
      </div>
    </Q>
    <Q title="Is there a power outlet near where the equipment will live?" testid="aq-power"><Single options={["Yes", "No"]} value={a.power} onChange={(v) => set({ power: v })} testid="aq-power-opt" cols="grid-cols-2 max-w-xs" /></Q>
    <Q title="Is strong Wi-Fi or hardwired Ethernet available at the equipment location?" testid="aq-network"><Single options={D.NETWORK} value={a.network} onChange={(v) => set({ network: v })} testid="aq-network-opt" cols="sm:grid-cols-3" /></Q>
    <Q title="Where will the equipment live?" testid="aq-location"><Single options={D.EQUIP_LOCATIONS} value={a.location} onChange={(v) => set({ location: v })} testid="aq-location-opt" cols="grid-cols-2 sm:grid-cols-5" /></Q>
  </>
);

export const OutdoorStep = ({ a, set }) => (
  <>
    <Q title="Will this setup include outdoor speakers?" testid="aq-outdoor">
      <Single options={["Yes", "No"]} value={a.outdoor} onChange={(v) => set({ outdoor: v })} testid="aq-outdoor-opt" cols="grid-cols-2 max-w-xs" />
    </Q>
    {a.outdoor === "Yes" && (
      <div data-testid="aq-outdoor-details">
        <Q title="How many outdoor speakers?"><Counter value={a.outQty} onChange={(v) => set({ outQty: v })} min={1} testid="aq-outqty" /></Q>
        <Q title="Locations"><Multi options={D.OUTDOOR_LOCATIONS} value={a.outLoc} onChange={(v) => set({ outLoc: v })} testid="aq-outloc" /></Q>
        <Q title="Speaker style"><Single options={D.OUTDOOR_STYLES} value={a.outStyle} onChange={(v) => set({ outStyle: v })} testid="aq-outstyle" /></Q>
      </div>
    )}
  </>
);
